import { useMemo, useState, type ReactNode } from "react";
import type { TFunction } from "i18next";
import { Trans, useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CheckboxCard } from "@/components/CheckboxCard";
import { Checkbox } from "@/components/ui/checkbox";
import { FormActions } from "@jbnl/shared-ui";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FieldLegend,
  FieldSet,
} from "@/components/ui/field";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { datumLang } from "@/i18n/datum";
import {
  VOORKEUR,
  type Inschrijving,
  type InschrijvingVelden,
} from "./useInschrijving";

/**
 * Shared by the inschrijven modal and an entered ploeg's card: the API takes the same body for
 * both, and a second copy of this form is the copy that forgets a rule.
 */
function vlaggenVan(velden: InschrijvingVelden | undefined) {
  return {
    samen: velden?.wilSamenOpEenDag ?? false,
    vr: velden?.deeltVrVoertuig ?? false,
  };
}

export function PloegInschrijvingFields({
  id,
  inschrijving,
  velden,
  standaardNaam,
  submitLabel,
  pending,
  error,
  gesloten,
  leadingAction,
  onSubmit,
}: {
  /** Prefixes every control's id, so two of these on one page do not collide. */
  id: string;
  inschrijving: Inschrijving;
  /** What the korps has already said. Absent for a ploeg being entered. */
  velden?: InschrijvingVelden;
  /**
   * The name an empty field gets. The server derives it either way; this is only what the
   * placeholder promises, so it comes from the API rather than being guessed here.
   */
  standaardNaam: string;
  submitLabel: string;
  pending: boolean;
  error: Error | null;
  /**
   * Always false here: a beheerder is standing in, which is who §4.4.4 lets past a deadline. The
   * Aanmeldsite's copy passes the window instead (ADR-0031).
   */
  gesloten: boolean;
  /**
   * Sits at the far left of the action row, a whole row away from the submit button on the
   * right. Where withdrawing a ploeg goes - the caller owns it, because entering a ploeg has
   * nothing to withdraw yet.
   */
  leadingAction?: ReactNode;
  onSubmit: (velden: InschrijvingVelden) => void;
}) {
  const { t } = useTranslation(["ploegen", "common"]);

  const [naam, setNaam] = useState(velden?.name ?? "");
  const [klasseIds, setKlasseIds] = useState(velden?.klasseIds ?? []);

  // One answer set per klasse (ADR-0049), because a ploeg is planned per klasse and so is asked
  // per klasse. Within a klasse the voorkeuren are an ordered list rather than a rank per dag:
  // the rank *is* the order, so holding it that way means it cannot go out of step with itself -
  // no gap at 2, no two firsts, nothing to renumber when one is unticked.
  //
  // Seeded already narrowed, so what is stored is never wider than what is on screen - a saved
  // voorkeur for a dag its own klasse is not run on is dropped on the way in rather than riding
  // along invisibly to the next save, and the server would now refuse it.
  const [voorkeuren, setVoorkeuren] = useState(() =>
    lees(inschrijving, velden, VOORKEUR.voorkeur),
  );

  const [geenVoorkeuren, setGeenVoorkeuren] = useState(() =>
    lees(inschrijving, velden, VOORKEUR.geenVoorkeur),
  );

  // The korps's own answer, which a beheerder standing in may change like any other field
  // (ADR-0052). Off for a ploeg being entered - the answer that asks the planner for nothing.
  const begin = vlaggenVan(velden);
  const [samen, setSamen] = useState(begin.samen);
  const [vr, setVr] = useState(begin.vr);

  /**
   * The periode's own numbers, because a beheerder namens a korps fills in the korps's form: a
   * cap of two over a periode allowing four would silently drop what they ticked.
   */
  const maxVoorkeuren = Number(inschrijving.maxVoorkeuren);
  const maxGeenVoorkeuren = Number(inschrijving.maxGeenVoorkeuren);

  const voorkeurenRegels = berekenVoorkeurenRegels(
    t,
    maxVoorkeuren,
    maxGeenVoorkeuren,
  );

  const [sortering, setSortering] = useState<"datum" | "reistijd">("datum");

  /**
   * Unticking drops that klasse's answers outright (ADR-0049): the server refuses a voorkeur
   * naming a klasse the ploeg is not in.
   */
  function toggleKlasse(klasseId: string) {
    const eraf = klasseIds.includes(klasseId);

    setKlasseIds(
      eraf
        ? klasseIds.filter((id) => id !== klasseId)
        : [...klasseIds, klasseId],
    );

    if (eraf) {
      setVoorkeuren((huidig) => zonder(huidig, klasseId));
      setGeenVoorkeuren((huidig) => zonder(huidig, klasseId));
    }
  }

  function toggleVoorkeur(klasseId: string, dagId: string) {
    setVoorkeuren((huidig) => wissel(huidig, klasseId, dagId));

    // A dag holds one answer within a klasse, so preferring it takes back ruling it out - and
    // only there: the same dag may still be ruled out for another klasse of this ploeg.
    setGeenVoorkeuren((huidig) => verwijder(huidig, klasseId, dagId));
  }

  function toggleGeenVoorkeur(klasseId: string, dagId: string) {
    setGeenVoorkeuren((huidig) => wissel(huidig, klasseId, dagId));
    setVoorkeuren((huidig) => verwijder(huidig, klasseId, dagId));
  }

  /**
   * Narrowed per klasse, not to the union, which let a beheerder rank a dag one half of the
   * ploeg could never be placed on (ADR-0049).
   */
  const tabellen = useMemo(
    () =>
      inschrijving.klassen
        .filter((klasse) => klasseIds.includes(klasse.id))
        .map((klasse) => ({
          klasse,
          dagen: gesorteerd(
            inschrijving.wedstrijddagen.filter((dag) =>
              dag.klasseIds.includes(klasse.id),
            ),
            sortering,
          ),
        })),
    [inschrijving.klassen, inschrijving.wedstrijddagen, klasseIds, sortering],
  );

  return (
    <form
      className="space-y-5"
      onSubmit={(event) => {
        event.preventDefault();
        onSubmit({
          name: naam,
          klasseIds,
          wilSamenOpEenDag: samen,
          deeltVrVoertuig: vr,
          // Flattened out of the per-klasse maps, every row carrying the klasse it was answered
          // under. The rank restarts at 1 inside each klasse: two klassen each have a first
          // choice, and there is no order between them (ADR-0049).
          voorkeuren: [
            ...klasseIds.flatMap((klasseId) =>
              (voorkeuren[klasseId] ?? []).map((wedstrijddagId, index) => ({
                klasseId,
                wedstrijddagId,
                kind: VOORKEUR.voorkeur,
                rank: index + 1,
              })),
            ),
            ...klasseIds.flatMap((klasseId) =>
              (geenVoorkeuren[klasseId] ?? []).map((wedstrijddagId) => ({
                klasseId,
                wedstrijddagId,
                kind: VOORKEUR.geenVoorkeur,
                rank: 0,
              })),
            ),
          ],
        });
      }}
    >
      {/* Optional: the derived name is what JBNL prints, but a korps with two teams knows which
        is which better than "2" does. */}
      <Field>
        <FieldLabel htmlFor={`${id}-naam`}>{t("velden.naam")}</FieldLabel>
        <Input
          id={`${id}-naam`}
          value={naam}
          maxLength={200}
          disabled={gesloten}
          placeholder={standaardNaam}
          onChange={(event) => setNaam(event.target.value)}
        />
        <FieldDescription>
          <Trans
            t={t}
            i18nKey="velden.naamUitleg"
            values={{ standaard: standaardNaam }}
            components={{ strong: <strong /> }}
          />
        </FieldDescription>
      </Field>

      {/* No "alles selecteren", unlike the house pattern: every klasse at once is a combination
        ADR-0033 always refuses. */}
      <FieldSet disabled={gesloten}>
        <FieldLegend variant="label">{t("velden.klassen")}</FieldLegend>
        <ul className="grid gap-2 sm:grid-cols-2">
          {inschrijving.klassen.map((klasse) => (
            <li key={klasse.id}>
              <CheckboxCard
                id={`${id}-klasse-${klasse.id}`}
                title={klasse.name}
                checked={klasseIds.includes(klasse.id)}
                disabled={gesloten}
                onCheckedChange={() => toggleKlasse(klasse.id)}
              />
            </li>
          ))}
        </ul>
        <FieldDescription>{t("velden.klassenUitleg")}</FieldDescription>
      </FieldSet>

      {/* One sort for every table: it is a way of reading the calendar, not an answer about a klasse. */}
      <div className="flex flex-wrap items-center gap-4">
        <SorteerKnoppen t={t} sortering={sortering} onSorteren={setSortering} />
      </div>

      {/* A table per klasse, because a ploeg is planned per klasse (ADR-0049): one table applied
        an answer to klassen the korps was never asked about. */}
      {tabellen.map(({ klasse, dagen }) => (
        <KlasseTabel
          key={klasse.id}
          t={t}
          inschrijving={inschrijving}
          klasse={klasse}
          dagen={dagen}
          voorkeuren={voorkeuren[klasse.id] ?? []}
          geenVoorkeuren={geenVoorkeuren[klasse.id] ?? []}
          maxVoorkeuren={maxVoorkeuren}
          maxGeenVoorkeuren={maxGeenVoorkeuren}
          gesloten={gesloten}
          onVoorkeur={(dagId) => toggleVoorkeur(klasse.id, dagId)}
          onGeenVoorkeur={(dagId) => toggleGeenVoorkeur(klasse.id, dagId)}
        />
      ))}

      {/* Drawn for every ploeg including a korps's first, with conditional wording. Below the
        tables because the planner reads a voorkeursdatum first (ADR-0052). */}
      <Field orientation="horizontal">
        <Switch
          id={`${id}-samen`}
          checked={samen}
          disabled={gesloten}
          onCheckedChange={(aan) => setSamen(aan === true)}
        />
        <FieldContent>
          <FieldLabel htmlFor={`${id}-samen`}>
            {t("velden.samen.label")}
          </FieldLabel>
          <FieldDescription>{t("velden.samen.description")}</FieldDescription>
        </FieldContent>
      </Field>

      <Field orientation="horizontal">
        <Switch
          id={`${id}-vr`}
          checked={vr}
          disabled={gesloten}
          onCheckedChange={(aan) => setVr(aan === true)}
        />
        <FieldContent>
          <FieldLabel htmlFor={`${id}-vr`}>{t("velden.vr.label")}</FieldLabel>
          <FieldDescription>{t("velden.vr.description")}</FieldDescription>
        </FieldContent>
      </Field>

      {/*
        Once below the tables rather than once inside each: it is the same sentence about every
        one of them, and repeating it under three tables reads as three different rules.
      */}
      {tabellen.length > 0 && (
        <FieldDescription>
          {t("velden.voorkeurenUitleg", { regels: voorkeurenRegels.join(" ") })}
        </FieldDescription>
      )}

      <FormActions className="border-t pt-4" leading={leadingAction}>
        <InschrijvingSubmitActions
          t={t}
          pending={pending}
          gesloten={gesloten}
          submitLabel={submitLabel}
          error={error}
        />
      </FormActions>
    </form>
  );
}

/** One sort for every table on the page, so the same two buttons never disagree between them. */
function SorteerKnoppen({
  t,
  sortering,
  onSorteren,
}: {
  t: TFunction<["ploegen", "common"]>;
  sortering: "datum" | "reistijd";
  onSorteren: (sortering: "datum" | "reistijd") => void;
}) {
  return (
    <div className="flex items-center gap-1 text-sm">
      <span className="text-muted-foreground">{t("velden.sorteerOp")}</span>
      <Button
        type="button"
        size="sm"
        variant={sortering === "datum" ? "secondary" : "ghost"}
        onClick={() => onSorteren("datum")}
      >
        {t("velden.sorteerDatum")}
      </Button>
      <Button
        type="button"
        size="sm"
        variant={sortering === "reistijd" ? "secondary" : "ghost"}
        onClick={() => onSorteren("reistijd")}
      >
        {t("velden.sorteerReistijd")}
      </Button>
    </div>
  );
}

/**
 * Disabled rather than hidden while the window is shut: a control that vanishes reads as
 * a screen that failed to load, and the card above already says why nothing can move.
 */
function InschrijvingSubmitActions({
  t,
  pending,
  gesloten,
  submitLabel,
  error,
}: {
  t: TFunction<["ploegen", "common"]>;
  pending: boolean;
  gesloten: boolean;
  submitLabel: string;
  error: Error | null;
}) {
  return (
    <>
      {error && <p className="text-sm text-destructive">{error.message}</p>}
      <Button type="submit" disabled={pending || gesloten}>
        {pending ? t("common:actions.busy") : submitLabel}
      </Button>
    </>
  );
}

/**
 * Same split as the Aanmeldsite's own `wizard.voorkeuren.description`: each half is a full
 * sentence with its own count, because a beheerder may turn either off and `_one`/`_other`
 * cannot span two independent numbers in one string.
 */
function berekenVoorkeurenRegels(
  t: TFunction<["ploegen", "common"]>,
  maxVoorkeuren: number,
  maxGeenVoorkeuren: number,
) {
  const regels: string[] = [];
  if (maxVoorkeuren > 0) {
    regels.push(t("velden.regelVoorkeur", { count: maxVoorkeuren }));
  }
  if (maxGeenVoorkeuren > 0) {
    regels.push(t("velden.regelGeenVoorkeur", { count: maxGeenVoorkeuren }));
  }
  return regels;
}

/**
 * An uncomputed pair sorts last rather than nearest: absent means unknown (ADR-0013), so it
 * cannot stand in for zero. `Number(...)`, because the generated client types an `int32` as
 * `number | string`.
 */
function reistijd(seconds: number | string | null | undefined) {
  return seconds === null || seconds === undefined
    ? Number.MAX_SAFE_INTEGER
    : Number(seconds);
}

/**
 * The reistijd as a korps reads it: a rounded drive, never the seconds the planner sorts on.
 * Absent means nobody has computed this pair yet, which is said rather than left blank.
 */
function reistijdLabel(
  t: TFunction<["ploegen", "common"]>,
  seconds: number | string | null | undefined,
) {
  if (seconds === null || seconds === undefined) {
    return t("reistijd.onbekend");
  }

  const minuten = Math.round(Number(seconds) / 60);
  if (minuten < 60) {
    return t("reistijd.minuten", { minuten });
  }

  const uren = Math.floor(minuten / 60);
  const rest = minuten % 60;
  return rest === 0
    ? t("reistijd.uren", { uren })
    : t("reistijd.urenMinuten", { uren, minuten: rest });
}

/**
 * One calendar per klasse, independent of the others because the two are planned separately
 * (ADR-0049). A table, not a CSS grid, which let the headers drift out of line with the boxes.
 * Two answer columns: a dag with neither ticked is possible, which is the absence of a row.
 */
function KlasseTabel({
  t,
  inschrijving,
  klasse,
  dagen,
  voorkeuren,
  geenVoorkeuren,
  maxVoorkeuren,
  maxGeenVoorkeuren,
  gesloten,
  onVoorkeur,
  onGeenVoorkeur,
}: {
  t: TFunction<["ploegen", "common"]>;
  inschrijving: Inschrijving;
  klasse: Inschrijving["klassen"][number];
  dagen: Inschrijving["wedstrijddagen"];
  voorkeuren: string[];
  geenVoorkeuren: string[];
  maxVoorkeuren: number;
  maxGeenVoorkeuren: number;
  gesloten: boolean;
  onVoorkeur: (dagId: string) => void;
  onGeenVoorkeur: (dagId: string) => void;
}) {
  const volleVoorkeuren = voorkeuren.length >= maxVoorkeuren;
  const volleGeenVoorkeuren = geenVoorkeuren.length >= maxGeenVoorkeuren;

  return (
    <FieldSet disabled={gesloten}>
      {/* Names the fieldset rather than heading it, so a screen reader says the klasse with every
        checkbox - the one thing telling two identical tables apart. */}
      <FieldLegend variant="label">
        {t("velden.voorkeurenKlasse", { klasse: klasse.name })}
      </FieldLegend>

      <div className="overflow-x-auto rounded-md border bg-background">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("velden.kolomDag")}</TableHead>
              <TableHead className="w-32">
                {t("velden.kolomReistijd")}
              </TableHead>
              {/*
                A column per half the periode actually asks about. Dropped rather than disabled:
                a greyed-out column is a promise of something a korps cannot have.
              */}
              {maxVoorkeuren > 0 && (
                <TableHead className="w-24 text-center">
                  {t("velden.kolomVoorkeur")}
                </TableHead>
              )}
              {maxGeenVoorkeuren > 0 && (
                <TableHead className="w-36 text-center">
                  {t("velden.kolomGeenVoorkeur")}
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {dagen.map((dag) => (
              <DagRij
                key={dag.id}
                t={t}
                inschrijving={inschrijving}
                klasse={klasse}
                dag={dag}
                voorkeuren={voorkeuren}
                geenVoorkeuren={geenVoorkeuren}
                maxVoorkeuren={maxVoorkeuren}
                maxGeenVoorkeuren={maxGeenVoorkeuren}
                volleVoorkeuren={volleVoorkeuren}
                volleGeenVoorkeuren={volleGeenVoorkeuren}
                gesloten={gesloten}
                onVoorkeur={() => onVoorkeur(dag.id)}
                onGeenVoorkeur={() => onGeenVoorkeur(dag.id)}
              />
            ))}

            {dagen.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={aantalKolommen(maxVoorkeuren, maxGeenVoorkeuren)}
                  className="text-sm text-muted-foreground"
                >
                  {t("velden.geenDagen", { klasse: klasse.name })}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </FieldSet>
  );
}

/** Every table's column count follows the same two toggles as its header. */
function aantalKolommen(maxVoorkeuren: number, maxGeenVoorkeuren: number) {
  return 2 + (maxVoorkeuren > 0 ? 1 : 0) + (maxGeenVoorkeuren > 0 ? 1 : 0);
}

/** The two checkbox cells share one shape: ticking is capped, not replaced, at the count a
 * beheerder set for the periode. */
function VoorkeurCheckboxCel({
  t,
  datum,
  klasseNaam,
  ariaLabelKey,
  aangevinkt,
  vol,
  gesloten,
  onToggle,
}: {
  t: TFunction<["ploegen", "common"]>;
  datum: string;
  klasseNaam: string;
  ariaLabelKey: "velden.vinkVoorkeur" | "velden.vinkGeenVoorkeur";
  aangevinkt: boolean;
  vol: boolean;
  gesloten: boolean;
  onToggle: () => void;
}) {
  return (
    <TableCell className="text-center">
      <Checkbox
        checked={aangevinkt}
        // Disabled at the cap rather than pushing the oldest out: a tick past
        // the limit silently dropping the first is a change nobody asked for.
        disabled={gesloten || (vol && !aangevinkt)}
        // The klasse is in the label, because the same date appears in every
        // table on this page.
        aria-label={t(ariaLabelKey, { datum, klasse: klasseNaam })}
        onCheckedChange={onToggle}
      />
    </TableCell>
  );
}

/** One dag's row: two badges for what is already answered, two capped checkboxes for what can
 * still be ticked. */
function DagRij({
  t,
  inschrijving,
  klasse,
  dag,
  voorkeuren,
  geenVoorkeuren,
  maxVoorkeuren,
  maxGeenVoorkeuren,
  volleVoorkeuren,
  volleGeenVoorkeuren,
  gesloten,
  onVoorkeur,
  onGeenVoorkeur,
}: {
  t: TFunction<["ploegen", "common"]>;
  inschrijving: Inschrijving;
  klasse: Inschrijving["klassen"][number];
  dag: Inschrijving["wedstrijddagen"][number];
  voorkeuren: string[];
  geenVoorkeuren: string[];
  maxVoorkeuren: number;
  maxGeenVoorkeuren: number;
  volleVoorkeuren: boolean;
  volleGeenVoorkeuren: boolean;
  gesloten: boolean;
  onVoorkeur: () => void;
  onGeenVoorkeur: () => void;
}) {
  const rang = voorkeuren.indexOf(dag.id);
  const isGeenVoorkeur = geenVoorkeuren.includes(dag.id);
  const datum = datumLang(dag.date);

  return (
    <TableRow>
      <TableCell>
        <span className="font-medium">{datum}</span>
        {rang >= 0 && (
          <Badge className="ml-2">
            {t("velden.badgeVoorkeur", { rang: rang + 1 })}
          </Badge>
        )}
        {isGeenVoorkeur && (
          <Badge variant="destructive" className="ml-2">
            {t("velden.badgeGeenVoorkeur")}
          </Badge>
        )}
        <span className="block text-xs text-muted-foreground">
          {dag.niveauName} · {dag.organiserendKorpsName} ·{" "}
          {klassenOpDag(t, inschrijving, dag.klasseIds)}
        </span>
      </TableCell>

      <TableCell className="text-sm whitespace-nowrap text-muted-foreground">
        {reistijdLabel(t, dag.reistijdSeconds)}
      </TableCell>

      {maxVoorkeuren > 0 && (
        <VoorkeurCheckboxCel
          t={t}
          datum={datum}
          klasseNaam={klasse.name}
          ariaLabelKey="velden.vinkVoorkeur"
          aangevinkt={rang >= 0}
          vol={volleVoorkeuren}
          gesloten={gesloten}
          onToggle={onVoorkeur}
        />
      )}

      {maxGeenVoorkeuren > 0 && (
        <VoorkeurCheckboxCel
          t={t}
          datum={datum}
          klasseNaam={klasse.name}
          ariaLabelKey="velden.vinkGeenVoorkeur"
          aangevinkt={isGeenVoorkeur}
          vol={volleGeenVoorkeuren}
          gesloten={gesloten}
          onToggle={onGeenVoorkeur}
        />
      )}
    </TableRow>
  );
}

/** The calendar in the order the beheerder asked for it. */
function gesorteerd(
  dagen: Inschrijving["wedstrijddagen"],
  sortering: "datum" | "reistijd",
) {
  if (sortering === "datum") {
    // The ISO date, where lexicographic order and chronological order are the same thing.
    return [...dagen].sort((a, b) => a.date.localeCompare(b.date));
  }

  return [...dagen].sort(
    (a, b) =>
      reistijd(a.reistijdSeconds) - reistijd(b.reistijdSeconds) ||
      a.date.localeCompare(b.date),
  );
}

/**
 * Narrowed to the dagen a klasse is run on; the voorkeuren come back in rank order, so position
 * is rank. `Number(rank)`, because a `"1"` from the generated client would sort as text.
 */
function lees(
  inschrijving: Inschrijving,
  velden: InschrijvingVelden | undefined,
  kind: number,
) {
  const klasseIds = velden?.klasseIds ?? [];
  const dagenPerKlasse = dagenPerKlasseMap(inschrijving);

  const perKlasse: Record<string, string[]> = {};

  for (const voorkeur of (velden?.voorkeuren ?? [])
    .filter(
      (v) => v.kind === kind && hoortBijKlasse(v, klasseIds, dagenPerKlasse),
    )
    .sort((a, b) => Number(a.rank) - Number(b.rank))) {
    (perKlasse[voorkeur.klasseId] ??= []).push(voorkeur.wedstrijddagId);
  }

  return perKlasse;
}

/** Every klasse's own dagen, so a voorkeur naming one it does not run on can be recognised. */
function dagenPerKlasseMap(inschrijving: Inschrijving) {
  return new Map(
    inschrijving.klassen.map((klasse) => [
      klasse.id,
      new Set(
        inschrijving.wedstrijddagen
          .filter((dag) => dag.klasseIds.includes(klasse.id))
          .map((dag) => dag.id),
      ),
    ]),
  );
}

/** A voorkeur only counts if the ploeg is still in that klasse and the klasse still runs that dag. */
function hoortBijKlasse(
  voorkeur: InschrijvingVelden["voorkeuren"][number],
  klasseIds: string[],
  dagenPerKlasse: Map<string, Set<string>>,
) {
  return (
    klasseIds.includes(voorkeur.klasseId) &&
    (dagenPerKlasse.get(voorkeur.klasseId)?.has(voorkeur.wedstrijddagId) ??
      false)
  );
}

/** One klasse's answers dropped whole - what unticking that klasse means. */
function zonder(antwoorden: Record<string, string[]>, klasseId: string) {
  return Object.fromEntries(
    Object.entries(antwoorden).filter(([id]) => id !== klasseId),
  );
}

/** Tick or untick one dag within one klasse, leaving every other klasse alone. */
function wissel(
  antwoorden: Record<string, string[]>,
  klasseId: string,
  dagId: string,
) {
  const huidig = antwoorden[klasseId] ?? [];

  return {
    ...antwoorden,
    [klasseId]: huidig.includes(dagId)
      ? huidig.filter((id) => id !== dagId)
      : [...huidig, dagId],
  };
}

function verwijder(
  antwoorden: Record<string, string[]>,
  klasseId: string,
  dagId: string,
) {
  return {
    ...antwoorden,
    [klasseId]: (antwoorden[klasseId] ?? []).filter((id) => id !== dagId),
  };
}

/**
 * Which klassen a wedstrijddag hosts - information, never a rule. It is what lets a korps see
 * that Brandbestrijding sits on its own dag before they pick a voorkeur they cannot have; the
 * rule itself is enforced on the way in and nowhere else.
 */
function klassenOpDag(
  t: TFunction<["ploegen", "common"]>,
  inschrijving: Inschrijving,
  klasseIds: string[],
) {
  const namen = inschrijving.klassen
    .filter((klasse) => klasseIds.includes(klasse.id))
    .map((klasse) => klasse.name);

  return namen.length > 0 ? namen.join(", ") : t("velden.geenKlassenOpDag");
}
